import React, { useEffect, useMemo, useRef, useState } from "react";

import { Link, useLocation, useParams } from "react-router-dom";

import HeaderOne from "../components/header/HeaderOne";
import Breadcrumb from "./Breadcrumb";
import FooterOne from "../components/footer/FooterOne";
import QuoteSection from "../components/contactform/QuoteSection";

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000"
).replace(/\/+$/, "");

function ServiceDetails() {
  const { slug } = useParams();
  const location = useLocation();

  const [product, setProduct] = useState(null);
  const [categories, setCategories] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const editorRef = useRef(null);
  const [activeGalleryIndex, setActiveGalleryIndex] = useState(null);
  const gallerySliderRef = useRef(null);

  /*
  |--------------------------------------------------------------------------
  | Dil
  |--------------------------------------------------------------------------
  */
  const currentLang = useMemo(() => {
    const params = new URLSearchParams(location.search);

    return params.get("lang") || "tr";
  }, [location.search]);

  /*
  |--------------------------------------------------------------------------
  | Slug oluştur
  |--------------------------------------------------------------------------
  */
  const createSlug = (text = "") => {
    return String(text)
      .trim()
      .toLocaleLowerCase("tr-TR")
      .replace(/ç/g, "c")
      .replace(/ğ/g, "g")
      .replace(/ı/g, "i")
      .replace(/ö/g, "o")
      .replace(/ş/g, "s")
      .replace(/ü/g, "u")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  /*
  |--------------------------------------------------------------------------
  | Slug sonundaki ID
  |--------------------------------------------------------------------------
  */
  const getIdFromSlug = (value = "") => {
    const match = String(value).match(/-(\d+)$/);

    if (!match) {
      return null;
    }

    return Number(match[1]);
  };

  const productId = getIdFromSlug(slug);

  /*
  |--------------------------------------------------------------------------
  | Dili linklerde koru
  |--------------------------------------------------------------------------
  */
  const withLang = (path) => {
    if (!currentLang || currentLang === "tr") {
      return path;
    }

    return `${path}?lang=${encodeURIComponent(currentLang)}`;
  };

  /*
  |--------------------------------------------------------------------------
  | Görsel
  |--------------------------------------------------------------------------
  */
  const getImageUrl = (image) => {
    if (!image) {
      return "";
    }

    let imagePath = image;

    if (typeof image === "object") {
      if (Array.isArray(image)) {
        imagePath = image[0]?.url || image[0]?.path || image[0]?.src || "";
      } else {
        imagePath = image.url || image.path || image.src || "";
      }
    }

    if (!imagePath || typeof imagePath !== "string") {
      return "";
    }

    if (
      imagePath.startsWith("http://") ||
      imagePath.startsWith("https://") ||
      imagePath.startsWith("data:") ||
      imagePath.startsWith("blob:")
    ) {
      return imagePath;
    }

    if (imagePath.startsWith("/cdn/")) {
      return `${API_BASE_URL}${imagePath}`;
    }

    const cleanImagePath = imagePath.replace(/^\/+/, "");

    return `${API_BASE_URL}/cdn/${cleanImagePath}`;
  };

  const normalizeImageList = (value) => {
    if (!value) return [];

    let parsedValue = value;

    if (typeof parsedValue === "string") {
      try {
        const json = JSON.parse(parsedValue);
        parsedValue = json;
      } catch {
        parsedValue = [parsedValue];
      }
    }

    if (!Array.isArray(parsedValue)) {
      parsedValue = [parsedValue];
    }

    return parsedValue
      .map((item) => {
        const src = getImageUrl(item);
        if (!src) return null;

        return {
          src,
          alt:
            (typeof item === "object" &&
              (item.alt || item.title || item.name || item.label)) ||
            "Galeri görseli",
        };
      })
      .filter(Boolean);
  };

  /*
  |--------------------------------------------------------------------------
  | Ürün + kategoriler
  |--------------------------------------------------------------------------
  */
  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      try {
        setLoading(true);
        setError("");

        if (!productId) {
          setError("Geçersiz ürün adresi.");
          return;
        }

        const [productResponse, categoryResponse] = await Promise.all([
          fetch(
            `${API_BASE_URL}/api/blogs/products/${productId}?lang=${encodeURIComponent(
              currentLang,
            )}`,
            {
              headers: {
                Accept: "application/json",
              },
              signal: controller.signal,
            },
          ),

          fetch(
            `${API_BASE_URL}/api/blogs/products/categories?lang=${encodeURIComponent(
              currentLang,
            )}`,
            {
              headers: {
                Accept: "application/json",
              },
              signal: controller.signal,
            },
          ),
        ]);

        if (!productResponse.ok) {
          if (productResponse.status === 404) {
            throw new Error("Ürün bulunamadı.");
          }

          throw new Error(`Ürün alınamadı: ${productResponse.status}`);
        }

        if (!categoryResponse.ok) {
          throw new Error(`Kategoriler alınamadı: ${categoryResponse.status}`);
        }
        const productData = await productResponse.json();

        const categoryData = await categoryResponse.json();

        setProduct(productData);

        setCategories(Array.isArray(categoryData) ? categoryData : []);

        /*
|--------------------------------------------------------------------------
| AYNI KATEGORİDEKİ DİĞER ÜRÜNLER
|--------------------------------------------------------------------------
*/
        setRelatedProducts([]);

        const primaryCategoryId =
          productData?.categoryIds?.[0] ||
          productData?.categories?.[0]?.id ||
          null;

        if (primaryCategoryId) {
          try {
            setRelatedLoading(true);

            const relatedResponse = await fetch(
              `${API_BASE_URL}/api/blogs/products?lang=${encodeURIComponent(
                currentLang,
              )}&category=${primaryCategoryId}`,
              {
                headers: {
                  Accept: "application/json",
                },
                signal: controller.signal,
              },
            );

            if (relatedResponse.ok) {
              const relatedData = await relatedResponse.json();

              const relatedList = Array.isArray(relatedData) ? relatedData : [];

              setRelatedProducts(relatedList);
            } else {
              setRelatedProducts([]);
            }
          } catch (relatedError) {
            if (relatedError.name !== "AbortError") {
              console.error("RELATED_PRODUCTS_ERROR:", relatedError);

              setRelatedProducts([]);
            }
          } finally {
            if (!controller.signal.aborted) {
              setRelatedLoading(false);
            }
          }
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("PRODUCT_DETAIL_ERROR:", err);

          setError(err.message || "Ürün yüklenirken bir hata oluştu.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      controller.abort();
    };
  }, [productId, currentLang]);

  /*
  |--------------------------------------------------------------------------
  | Ürünün bağlı olduğu kategori ID'leri
  |--------------------------------------------------------------------------
  */
  const productCategoryIds = product?.categoryIds?.map(Number) || [];

  /*
  |--------------------------------------------------------------------------
  | İçerik
  |--------------------------------------------------------------------------
  */
  const editorContent =
    product?.data?.editor ||
    product?.content ||
    product?.data?.content ||
    product?.description ||
    "";

  const productImage = getImageUrl(
    product?.image ||
      product?.data?.image ||
      product?.data?.computerImage ||
      product?.computerImg,
  );

  const title = product?.title || product?.data?.title || "Ürün Detayı";
  const galleryImages = normalizeImageList(
    product?.gallery ||
      product?.data?.gallery ||
      product?.galleryImage ||
      product?.data?.galleryImage,
  );

  const breadcrumbs = [
    {
      label: "Ana Sayfa",
      link: withLang("/"),
    },
    {
      label: "Ürünler ve Müşterilerimizin Uygulamaları",
      link: withLang("/our-service"),
    },
    {
      label: title,
    },
  ];

  const openGalleryImage = (index) => {
    setActiveGalleryIndex(index);
  };

  const closeGalleryImage = () => {
    setActiveGalleryIndex(null);
  };

  const showPrevGalleryImage = () => {
    if (!galleryImages.length) return;
    setActiveGalleryIndex((prev) =>
      prev === 0 ? galleryImages.length - 1 : prev - 1,
    );
  };

  const showNextGalleryImage = () => {
    if (!galleryImages.length) return;
    setActiveGalleryIndex((prev) =>
      prev === galleryImages.length - 1 ? 0 : prev + 1,
    );
  };

  const scrollGallery = (direction) => {
    const slider = gallerySliderRef.current;
    if (!slider) return;

    const firstItem = slider.querySelector(".demirsan-gallery-item");
    if (!firstItem) return;

    const itemWidth = firstItem.getBoundingClientRect().width;

    const styles = window.getComputedStyle(slider);
    const gap = parseFloat(styles.columnGap || styles.gap || 0);

    slider.scrollBy({
      left: direction === "right" ? itemWidth + gap : -(itemWidth + gap),
      behavior: "smooth",
    });
  };
  useEffect(() => {
    if (activeGalleryIndex === null) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeGalleryImage();
      }

      if (event.key === "ArrowLeft") {
        showPrevGalleryImage();
      }

      if (event.key === "ArrowRight") {
        showNextGalleryImage();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [activeGalleryIndex, galleryImages.length]);
  useEffect(() => {
    const root = editorRef.current;

    if (!root) return;

    const tables = root.querySelectorAll("table");

    tables.forEach((table) => {
      table.classList.add("demirsan-brand-table");

      const firstRow =
        table.querySelector("tbody tr") || table.querySelector("tr");

    const cellCount = firstRow
  ? Array.from(firstRow.cells).reduce(
      (total, cell) => total + cell.colSpan,
      0
    )
  : 0;

      if (cellCount <= 3) {
        table.classList.add("demirsan-spec-table");
      }

      if (cellCount >= 4) {
        table.classList.add("demirsan-wide-table");
        // CKEditor'ın sabit genişliklerini temizle — tarayıcı içeriğe göre hesaplasın
        table.style.tableLayout = "auto";
        table.style.width = "100%";
        table.querySelectorAll("col").forEach((col) => {
          col.removeAttribute("style");
          col.removeAttribute("width");
        });
        table.querySelectorAll("td, th").forEach((cell) => {
          cell.removeAttribute("width");
          if (cell.style.width) cell.style.width = "";
        });
        // Tüm başlık hücreleri sarabilsin
        const headerRow =
          table.querySelector("tbody tr") || table.querySelector("tr");
        if (headerRow) {
          Array.from(headerRow.cells).forEach((cell) => {
            cell.style.whiteSpace = "normal";
            cell.style.wordBreak = "break-word";
            cell.style.fontSize = "12px";
            cell.style.lineHeight = "1.3";
            // "KANGAL MIN. - MAKS." hücresine özel: max 2 satırda sığacak min-width
            const txt = (cell.textContent || "").toLocaleLowerCase("tr-TR");
            if (txt.includes("kangal")) {
              cell.style.minWidth = "90px";
            }
          });
        }
      }

      if (
        table.parentElement &&
        !table.parentElement.classList.contains("demirsan-table-wrapper")
      ) {
        const wrapper = document.createElement("div");
        wrapper.className = "demirsan-table-wrapper";
        table.parentNode.insertBefore(wrapper, table);
        wrapper.appendChild(table);
      }
    });
  }, [editorContent]);

  return (
    <div>
      <HeaderOne />

      <Breadcrumb title={title} breadcrumbs={breadcrumbs} />

      <style>
        {`
          .demirsan-detail-image {
            width: 100%;
            margin-bottom: 35px;
            padding: 25px;

            background: #f7f7f7;

            display: flex;
            align-items: center;
            justify-content: center;

            min-height: 420px;
          }

          .demirsan-detail-image img {
            display: block;
            width: 100%;
            max-height: 600px;
            object-fit: contain;
          }

          .demirsan-detail-title {
            margin-bottom: 25px;
            color: #111;
          }

.demirsan-editor-content {
  color: #111;
  font-size: 16px;
  line-height: 1.85;
}

          .demirsan-editor-content p {
            margin-bottom: 18px;
          }

          .demirsan-editor-content img {
            max-width: 100%;
            height: auto;
          }

          .demirsan-editor-content ul,
          .demirsan-editor-content ol {
            padding-left: 25px;
            margin-bottom: 20px;
          }

          .demirsan-category-widget
            .single-categories {
            margin: 0;
            padding: 0;
          }

          .demirsan-category-widget
            .single-categories
            li {
            list-style: none;
          }

          .demirsan-category-widget
            .single-categories
            a {
            display: flex;
            align-items: center;
            justify-content: space-between;

            width: 100%;
          }

  .demirsan-category-widget
  .single-categories
  a.active-category,
.demirsan-related-widget
  .single-categories
  a.active-category {
  color: #e20614;
  font-weight: 700;
}

          .demirsan-detail-loading,
          .demirsan-detail-error {
            padding: 70px 20px;
            text-align: center;
          }

          @media (max-width: 767px) {
            .demirsan-detail-image {
              min-height: 280px;
              padding: 15px;
            }
          }
            /* =========================================
   SOL MENÜ - KATEGORİLER + UYGULAMALAR
   AYNI ÖLÇÜDE KOMPAKT
========================================= */

.demirsan-category-widget .single-categories,
.demirsan-related-widget .single-categories {
  margin: 0 0 7px !important;
  padding: 0 !important;
}

.demirsan-category-widget .single-categories:last-child,
.demirsan-related-widget .single-categories:last-child {
  margin-bottom: 0 !important;
}

.demirsan-category-widget .single-categories li,
.demirsan-related-widget .single-categories li {
  margin: 0 !important;
  padding: 0 !important;
  list-style: none !important;
}

.demirsan-category-widget .single-categories li a,
.demirsan-related-widget .single-categories li a {
  display: flex !important;

  align-items: center !important;
  justify-content: space-between !important;

  width: 100% !important;

  min-height: 44px !important;

  margin: 0 !important;

  padding: 10px 14px !important;

  box-sizing: border-box !important;

  color: #4f5666;

  font-size: 14px !important;
  font-weight: 500;

  line-height: 1.35 !important;

  transition:
    color 0.2s ease,
    background-color 0.2s ease;
}

/* Hover */
.demirsan-category-widget .single-categories li a:hover,
.demirsan-related-widget .single-categories li a:hover {
  color: #e20614 !important;
}

/* Aktif kategori / aktif ürün */
.demirsan-category-widget .single-categories li a.active-category,
.demirsan-related-widget .single-categories li a.active-category {
  color: #e20614 !important;
  font-weight: 700 !important;
}

/* Sağdaki oklar da aynı */
.demirsan-category-widget .single-categories li a i,
.demirsan-related-widget .single-categories li a i {
  flex-shrink: 0;

  margin-left: 12px;

  color: #e20614;

  font-size: 13px !important;
}
  /* =========================================
   KATEGORİ + UYGULAMA HOVER DÜZELTME
========================================= */


.demirsan-category-widget .single-categories li a:hover,
.demirsan-related-widget .single-categories li a:hover {
  background: #e20614 !important;
  color: #ffffff !important;
}

/* Yazı */
.demirsan-category-widget .single-categories li a:hover span,
.demirsan-related-widget .single-categories li a:hover span {
  color: #ffffff !important;
}

/* Sağdaki ok */
.demirsan-category-widget .single-categories li a:hover i,
.demirsan-related-widget .single-categories li a:hover i {
  color: #ffffff !important;
}
  /* =========================================
   YEŞİL - ANA BÖLÜM ÜST / ALT BOŞLUK
   mevcut boşluğun yaklaşık %60'ı
========================================= */

.rts-service-details-area.rts-section-gap {
  padding-top: 72px !important;
  padding-bottom: 72px !important;
}


/* =========================================
   MAVİ - BÜYÜK BLOKLAR ARASI BOŞLUK
   yaklaşık %50
========================================= */

.demirsan-detail-image {
  margin-bottom: 18px !important;
  padding: 13px !important;
}

.demirsan-related-widget {
  margin-top: 15px !important;
}


/* =========================================
   MOR - SOL MENÜ SATIRLARI
   DAHA KOMPAKT
========================================= */

.demirsan-category-widget .single-categories li a,
.demirsan-related-widget .single-categories li a {
  min-height: 34px !important;
  padding: 6px 10px !important;
}
  /* =========================================================
   SOL KUTULARI DİKEY OLARAK DARALT
   Liste maddelerine dokunmaz
========================================================= */

.demirsan-category-widget,
.demirsan-related-widget {
  padding: 22px 24px !important;
}


/* =========================================================
   BAŞLIK ALANI
   Üst ve alt boşluk küçültüldü
========================================================= */

.demirsan-category-widget .wized-header,
.demirsan-related-widget .wized-header {
  display: flex !important;
  align-items: center !important;

  min-height: 48px !important;

  margin: 0 0 14px 0 !important;
  padding: 0 !important;
}


/* Başlık kendi alanının ortasında */
.demirsan-category-widget .wized-header .title,
.demirsan-related-widget .wized-header .title {
  margin: 0 !important;
  padding: 0 !important;

  line-height: 1.25 !important;
}


/* =========================================================
   LİSTENİN ALTINDA GEREKSİZ BOŞLUK KALMASIN
========================================================= */

.demirsan-category-widget .wized-body,
.demirsan-related-widget .wized-body {
  margin: 0 !important;
  padding: 0 !important;
}


/* =========================================================
   ÜRÜN KATEGORİLERİ İLE UYGULAMALAR
   ARASINDAKİ BOŞLUĞU AZALT
========================================================= */

.demirsan-related-widget.mt--30 {
  margin-top: 14px !important;
}
  .demirsan-editor-content table {
  width: 100%;
  border-collapse: collapse;
  margin: 20px 0;
}

.demirsan-editor-content table th,
.demirsan-editor-content table td {
  border: 1px solid #ddd;
  padding: 10px 12px;
  text-align: left;
  vertical-align: middle;
}

.demirsan-editor-content table th {
  background: #f5f5f5;
  font-weight: 600;
  color: #222;
}

.demirsan-editor-content table tbody tr:nth-child(even) {
  background: #fafafa;
}

.demirsan-editor-content {
  overflow-x: auto;
}

.demirsan-editor-content figure.table {
  width: 100%;
  margin: 20px 0;
  overflow-x: auto;
}
/* =========================================================
   DEMİRSAN MARKALI TABLOLAR
========================================================= */

.demirsan-table-wrapper {
  width: 100%;
  margin: 24px 0 34px;

  overflow-x: auto;
  overflow-y: hidden;

  border-radius: 14px;

  background: #ffffff;

  box-shadow:
    0 4px 10px rgba(10, 29, 56, 0.06),
    0 16px 35px rgba(10, 29, 56, 0.08);

  -webkit-overflow-scrolling: touch;
}


/* =========================================================
   INLINE EDITÖR ÖLÇÜLERİNİ EZ
========================================================= */

.demirsan-editor-content table.demirsan-brand-table {
  width: 100% !important;
  height: auto !important;

  margin: 0 !important;

  border: 0 !important;

  border-collapse: separate !important;
  border-spacing: 0 !important;

  background: #ffffff;

  overflow: hidden;
}


/* Editörden gelen garip col genişliklerini temizle */
.demirsan-editor-content
  table.demirsan-brand-table
  col {
  width: auto !important;
}


.demirsan-editor-content
  table.demirsan-brand-table
  tr {
  height: auto !important;
}


/* =========================================================
   BÜTÜN HÜCRELER
========================================================= */

.demirsan-editor-content
  table.demirsan-brand-table
  td,
.demirsan-editor-content
  table.demirsan-brand-table
  th {

  height: auto !important;

  padding: 15px 18px !important;

  border: 0 !important;

  border-right: 1px solid #dce3ec !important;
  border-bottom: 1px solid #dce3ec !important;

  vertical-align: middle;

  background: #ffffff;

  transition:
    background-color 0.2s ease,
    color 0.2s ease,
    box-shadow 0.2s ease;
}


/* =========================================================
   İLK SATIR = KIRMIZI TABLO BAŞLIĞI
   Senin HTML'de TH olmadığı için TR:first-child yakalıyoruz
========================================================= */

.demirsan-editor-content
  table.demirsan-brand-table
  tbody
  tr:first-child
  td {

  background: #e20614 !important;

  color: #ffffff !important;

  font-weight: 700;

  border-right:
    1px solid rgba(255, 255, 255, 0.25) !important;

  border-bottom: 0 !important;

  box-shadow: none;
}


/* =========================================================
   2 KOLONLU ÜRÜN ÖZELLİKLERİ TABLOSU
========================================================= */

/* Sol özellik sütunu */
.demirsan-editor-content
  table.demirsan-spec-table
  tbody
  tr:not(:first-child)
  td:first-child {

  background: rgba(226, 6, 20, 0.06) !important;

  color: #1a1a1a !important;

  font-weight: 600;

  border-right: 2px solid #e20614 !important;
}


/* Sağ değer sütunu */
.demirsan-editor-content
  table.demirsan-spec-table
  tbody
  tr:not(:first-child)
  td:not(:first-child) {

  background: #ffffff !important;
}


/* Alternatif satırlar */
.demirsan-editor-content
  table.demirsan-spec-table
  tbody
  tr:nth-child(odd):not(:first-child)
  td:not(:first-child) {

  background: #f4f7fb !important;
}


/* =========================================================
   HOVER - SATIRA HAREKET KAT
========================================================= */

.demirsan-editor-content
  table.demirsan-spec-table
  tbody
  tr:not(:first-child):hover
  td:not(:first-child) {

  background: rgba(226, 6, 20, 0.04) !important;
}


/* Hover olunca sol lacivert bölüm biraz değişsin */
.demirsan-editor-content
  table.demirsan-spec-table
  tbody
  tr:not(:first-child):hover
  td:first-child {

  background:
    linear-gradient(
      135deg,
      #172b4d 0%,
      #203b68 100%
    ) !important;

  box-shadow:
    inset 7px 0 0 #c12a59;
}


/* =========================================================
   GENİŞ PAKETLEME TABLOSU (JS sınıfı + CSS :has() çift güvence)
========================================================= */

/* JS sınıflı tablo */
.demirsan-editor-content
  table.demirsan-wide-table {
  min-width: 0 !important;
  width: 100% !important;
  table-layout: auto !important;
}

/* :has() — JS olmadan da 4+ kolonu olan tabloları yakala */
.demirsan-editor-content
  table:has(tbody tr td:nth-child(4)) {
  min-width: 0 !important;
  width: 100% !important;
  table-layout: auto !important;
}

/* Çift class ile genel brand-table kuralını kesinlikle ez */
body .demirsan-editor-content
  table.demirsan-brand-table.demirsan-wide-table
  tbody
  tr
  td,
body .demirsan-editor-content
  table:has(tbody tr td:nth-child(4))
  tbody
  tr
  td {

  padding: 12px 5px !important;

  font-size: 13px !important;
}

/* Başlık satırı — sarabilsin */
body .demirsan-editor-content
  table.demirsan-brand-table.demirsan-wide-table
  tbody
  tr:first-child
  td,
body .demirsan-editor-content
  table:has(tbody tr td:nth-child(4))
  tbody
  tr:first-child
  td {

  font-size: 12px !important;

  line-height: 1.7 !important;

  white-space: normal !important;

  word-break: break-word !important;

  min-width: 85px !important;

  padding: 14px 5px !important;
}

/* Veri hücreleri tek satırda kalsın */
body .demirsan-editor-content
  table.demirsan-brand-table.demirsan-wide-table
  tbody
  tr:not(:first-child)
  td,
body .demirsan-editor-content
  table:has(tbody tr td:nth-child(4))
  tbody
  tr:not(:first-child)
  td {

  white-space: nowrap !important;
}


/* İlk kolon koyu lacivert */
.demirsan-editor-content
  table.demirsan-wide-table
  tbody
  tr:not(:first-child)
  td:first-child {

  background: rgba(226, 6, 20, 0.06) !important;

  color: #1a1a1a !important;

  font-weight: 600;

  border-right: 2px solid #e20614 !important;
}


/* Geniş tablonun zebra satırları */
.demirsan-editor-content
  table.demirsan-wide-table
  tbody
  tr:nth-child(even):not(:first-child)
  td:not(:first-child) {

  background: #f5f7fa !important;
}


.demirsan-editor-content
  table.demirsan-wide-table
  tbody
  tr:nth-child(odd):not(:first-child)
  td:not(:first-child) {

  background: #ffffff !important;
}


/* Geniş tabloda hover */
.demirsan-editor-content
  table.demirsan-wide-table
  tbody
  tr:not(:first-child):hover
  td:not(:first-child) {

  background: rgba(226, 6, 20, 0.04) !important;
}


/* İlk kolon hover */
.demirsan-editor-content
  table.demirsan-wide-table
  tbody
  tr:not(:first-child):hover
  td:first-child {

  background: #1b355e !important;

  box-shadow:
    inset 7px 0 0 #c12a59;
}


/* =========================================================
   KENARLARI TEMİZLE
========================================================= */

.demirsan-editor-content
  table.demirsan-brand-table
  tr
  td:last-child {

  border-right: 0 !important;
}


.demirsan-editor-content
  table.demirsan-brand-table
  tbody
  tr:last-child
  td {

  border-bottom: 0 !important;
}


/* =========================================================
   TABLO İÇİNDE P VARSA FAZLA BOŞLUK OLMASIN
========================================================= */

.demirsan-editor-content
  table.demirsan-brand-table
  td
  p {

  margin: 0;
}


/* =========================================================
   MOBİL
========================================================= */

@media (max-width: 767px) {

  .demirsan-table-wrapper {
    border-radius: 10px;
    margin: 20px 0 28px;
  }

  .demirsan-editor-content
    table.demirsan-spec-table {

    min-width: 600px;
  }

  .demirsan-editor-content
    table.demirsan-wide-table {

    min-width: 1000px;
  }

  .demirsan-editor-content
    table.demirsan-brand-table
    td {

    padding: 12px 14px !important;
  }
}
  .demirsan-editor-content table {
  width: 100% !important;
  height: auto !important;

  margin: 26px 0 34px !important;

  border-collapse: separate !important;
  border-spacing: 0 !important;

  border: 1px solid #dfe3e8 !important;
  border-radius: 10px;

  overflow: hidden;

  background: #ffffff !important;

  box-shadow:
    0 4px 14px rgba(18, 40, 75, 0.06);
}


/* Editörden gelen sabit yükseklikleri kaldır */
.demirsan-editor-content table tr {
  height: auto !important;
}


/* Hücreler */
.demirsan-editor-content table td,
.demirsan-editor-content table th {
  height: auto !important;

  padding: 14px 18px !important;

  border: 0 !important;
  border-bottom: 1px solid #e5e8ec !important;

  vertical-align: middle !important;

  transition: background-color 0.2s ease;
}


/* ======================================
   SOL SÜTUN = KOYU LACİVERT
====================================== */

.demirsan-editor-content table tbody tr td:first-child {
  background: rgba(226, 6, 20, 0.06) !important;

  color: #1a1a1a !important;

  font-weight: 600 !important;

  border-right: 2px solid #e20614 !important;
}


/* Sağ taraf */
.demirsan-editor-content table tbody tr td:not(:first-child) {
  background: #ffffff !important;
}


/* Hafif zebra */
.demirsan-editor-content table tbody tr:nth-child(even) td:not(:first-child) {
  background: #f7f8fa !important;
}


/* Hover */
.demirsan-editor-content table tbody tr:hover td:not(:first-child) {
  background: rgba(226, 6, 20, 0.04) !important;
}


/* Sol taraf hover */
.demirsan-editor-content table tbody tr:hover td:first-child {
  background: rgba(226, 6, 20, 0.06) !important;
}


/* Son satır */
.demirsan-editor-content table tbody tr:last-child td {
  border-bottom: 0 !important;
}


/* Editörün bozuk col genişliklerini ez */
.demirsan-editor-content table col {
  width: auto !important;
}

  /* SOL SÜTUN */
.demirsan-editor-content table tbody tr td:first-child {
  background: rgba(226, 6, 20, 0.06) !important;
  color: #1a1a1a !important;
  font-weight: 600 !important;

  border-right: 2px solid #e20614 !important;
}


/* İLK SATIR - SOL */
.demirsan-editor-content table tbody tr:first-child td:first-child {
  background: #e20614 !important;
  color: #ffffff !important;
  font-weight: 700 !important;
}


/* İLK SATIR - SAĞ TARAF */
.demirsan-editor-content table tbody tr:first-child td:not(:first-child) {
  background: rgba(226, 6, 20, 0.06) !important;
  color: #1a1a1a !important;
  font-weight: 700 !important;

  border-bottom: 1px solid #e0e0e0 !important;
}


/* NORMAL SAĞ HÜCRELER */
.demirsan-editor-content table tbody tr td:not(:first-child) {
  background: #ffffff !important;
}


/* ALTERNATİF SATIR */
.demirsan-editor-content table tbody tr:nth-child(even) td:not(:first-child) {
  background: #f4f7fa !important;
}


/* HOVER */
.demirsan-editor-content table tbody tr:hover td:not(:first-child) {
  background: rgba(226, 6, 20, 0.04) !important;
}


/* SOL TARAF HOVER */
.demirsan-editor-content table tbody tr:hover td:first-child {
  background: rgba(226, 6, 20, 0.06) !important;
}
  /* Hücre çizgilerini belirginleştir */
.demirsan-editor-content table td,
.demirsan-editor-content table th {
  border-right: 1px solid #b8c3d1 !important;
  border-bottom: 1px solid #b8c3d1 !important;
}


/* Dış çerçeve biraz daha güçlü */
.demirsan-editor-content table {
  border: 1px solid #9eacbd !important;
}


/* Son sütunda fazladan sağ çizgi olmasın */
.demirsan-editor-content table tr td:last-child,
.demirsan-editor-content table tr th:last-child {
  border-right: 0 !important;
}


/* Son satırda fazladan alt çizgi olmasın */
.demirsan-editor-content table tbody tr:last-child td {
  border-bottom: 0 !important;
}

  .demirsan-gallery-section {
  margin-top: 42px;
}

.demirsan-gallery-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 18px;
}

.demirsan-gallery-title {
  margin: 0;
  font-size: 28px;
  line-height: 1.2;
  color: #111;
}

.demirsan-gallery-actions {
  display: flex;
  gap: 10px;
}

.demirsan-gallery-scroll-btn {
  width: 42px;
  height: 42px;
  border: 0;
  border-radius: 50%;
  background: #172d4d;
  color: #fff;
  cursor: pointer;
  transition: 0.2s ease;
}

.demirsan-gallery-scroll-btn:hover {
  background: #a82450;
}

.demirsan-gallery-slider {
  display: flex;
  gap: 16px;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  padding-bottom: 8px;
  -webkit-overflow-scrolling: touch;
}

.demirsan-gallery-slider::-webkit-scrollbar {
  height: 8px;
}

.demirsan-gallery-slider::-webkit-scrollbar-thumb {
  background: #c7cfda;
  border-radius: 999px;
}

.demirsan-gallery-item {
  flex: 0 0 260px;
  width: 260px;
  border: 1px solid #e5e8ee;
  border-radius: 16px;
  background: #fff;
  overflow: hidden;
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
  box-shadow: 0 8px 24px rgba(15, 30, 55, 0.06);
}

.demirsan-gallery-item:hover {
  transform: translateY(-4px);
  box-shadow: 0 14px 32px rgba(15, 30, 55, 0.12);
}

.demirsan-gallery-item-image {
  width: 100%;
  height: 190px;
  background: #f7f7f7;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px;
}

.demirsan-gallery-item-image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.demirsan-gallery-item-caption {
  padding: 12px 14px;
  font-size: 14px;
  color: #4f5666;
  text-align: center;
}

.demirsan-gallery-lightbox {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(10, 15, 24, 0.88);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 30px;
}

.demirsan-gallery-lightbox-content {
  position: relative;
  max-width: 92vw;
  max-height: 88vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.demirsan-gallery-lightbox-content img {
  max-width: 100%;
  max-height: 88vh;
  object-fit: contain;
  border-radius: 14px;
  background: #fff;
}

.demirsan-gallery-close {
  position: absolute;
  top: 18px;
  right: 18px;
  width: 46px;
  height: 46px;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  z-index: 2;
}

.demirsan-gallery-close:hover {
  background: rgba(255, 255, 255, 0.24);
}

.demirsan-gallery-nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 52px;
  height: 52px;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  font-size: 28px;
  cursor: pointer;
  z-index: 2;
}

.demirsan-gallery-nav-btn:hover {
  background: rgba(255, 255, 255, 0.24);
}

.demirsan-gallery-nav-btn.prev {
  left: 18px;
}

.demirsan-gallery-nav-btn.next {
  right: 18px;
}

@media (max-width: 767px) {
  .demirsan-gallery-title {
    font-size: 22px;
  }

  .demirsan-gallery-item {
    flex: 0 0 220px;
    width: 220px;
  }

  .demirsan-gallery-item-image {
    height: 160px;
  }

  .demirsan-gallery-lightbox {
    padding: 16px;
  }

  .demirsan-gallery-nav-btn {
    width: 44px;
    height: 44px;
    font-size: 24px;
  }

  .demirsan-gallery-close {
    width: 42px;
    height: 42px;
    font-size: 24px;
    top: 10px;
    right: 10px;
  }
}
  .demirsan-gallery-item {
  flex: 0 0 340px;
  width: 340px;

  border: 1px solid #e5e8ee;
  border-radius: 16px;
  background: #ffffff;
  overflow: hidden;
  cursor: pointer;

  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  box-shadow: 0 8px 24px rgba(15, 30, 55, 0.06);
}

.demirsan-gallery-item-image {
  width: 100%;
  height: 250px;

  background: #f7f7f7;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 12px;
}

.demirsan-gallery-item-image img {
  width: 100%;
  height: 100%;

  object-fit: contain;

  display: block;
}
  @media (max-width: 767px) {
  .demirsan-gallery-item {
    flex: 0 0 280px;
    width: 280px;
  }

  .demirsan-gallery-item-image {
    height: 210px;
  }
}
  /* =========================================================
   GALERİ ÜST SAĞ OK BUTONLARI
========================================================= */

.demirsan-gallery-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.demirsan-gallery-scroll-btn {
  width: 42px;
  height: 42px;

  padding: 0;
  margin: 0;

  border: 1px solid #dfe3e8;
  border-radius: 50%;

  background: #ffffff;
  color: #172d4d;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  font-size: 27px;
  font-weight: 400;
  line-height: 1;

  cursor: pointer;

  box-shadow: 0 4px 12px rgba(23, 45, 77, 0.08);

  transition:
    background-color 0.2s ease,
    color 0.2s ease,
    border-color 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.demirsan-gallery-scroll-btn:hover {
  background: #e20614;
  border-color: #e20614;
  color: #ffffff;

  transform: translateY(-1px);

  box-shadow: 0 6px 16px rgba(226, 6, 20, 0.22);
}

.demirsan-gallery-scroll-btn:active {
  transform: translateY(0);
}

.demirsan-gallery-scroll-btn:focus {
  outline: none;
}
  /* =========================================================
   BÜYÜK GÖRSEL - SAĞ / SOL OKLAR
========================================================= */

.demirsan-gallery-nav-btn {
  position: absolute;
  top: 50%;

  width: 54px;
  height: 54px;

  padding: 0;
  margin: 0;

  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 50%;

  background: rgba(15, 31, 58, 0.82);
  color: #ffffff;

  display: flex;
  align-items: center;
  justify-content: center;

  font-size: 32px;
  font-weight: 300;
  line-height: 1;

  cursor: pointer;
  z-index: 2;

  transform: translateY(-50%);

  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    transform 0.2s ease;
}

.demirsan-gallery-nav-btn:hover {
  background: #e20614;
  border-color: #e20614;
}

.demirsan-gallery-nav-btn.prev {
  left: 20px;
}

.demirsan-gallery-nav-btn.next {
  right: 20px;
}
  /* =========================================================
   LIGHTBOX KAPAT BUTONU
========================================================= */

.demirsan-gallery-close {
  position: absolute;
  top: 18px;
  right: 18px;

  width: 46px;
  height: 46px;

  padding: 0;
  margin: 0;

  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 50%;

  background: rgba(15, 31, 58, 0.82);
  color: #ffffff;

  display: flex;
  align-items: center;
  justify-content: center;

  font-size: 27px;
  font-weight: 300;
  line-height: 1;

  cursor: pointer;
  z-index: 3;

  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    transform 0.2s ease;
}

.demirsan-gallery-close:hover {
  background: #e20614;
  border-color: #e20614;
  transform: rotate(90deg);
}
  .demirsan-gallery-scroll-btn {
  width: 42px;
  height: 42px;

  padding: 0 !important;
  margin: 0;

  border: 1px solid #dfe3e8;
  border-radius: 50%;

  background: #ffffff;
  color: #172d4d;

  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;

  cursor: pointer;

  box-shadow: 0 4px 12px rgba(23, 45, 77, 0.08);

  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease,
    box-shadow 0.2s ease;
}

.demirsan-gallery-scroll-btn svg {
  width: 18px;
  height: 18px;

  display: block;

  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;

  flex-shrink: 0;
}

.demirsan-gallery-scroll-btn:hover {
  background: #e20614;
  border-color: #e20614;
  color: #ffffff;

  box-shadow: 0 6px 16px rgba(226, 6, 20, 0.2);
}
  .demirsan-gallery-nav-btn {
  position: absolute;
  top: 50%;

  width: 54px;
  height: 54px;

  padding: 0 !important;
  margin: 0;

  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 50%;

  background: #172d4d;
  color: #ffffff;

  display: flex !important;
  align-items: center !important;
  justify-content: center !important;

  cursor: pointer;
  z-index: 2;

  transform: translateY(-50%);
}

.demirsan-gallery-nav-btn svg {
  width: 22px;
  height: 22px;

  display: block;

  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;

  flex-shrink: 0;
}

.demirsan-gallery-nav-btn:hover {
  background: #e20614;
  border-color: #e20614;
}
  /* ============================
   GALERİ
============================ */

.demirsan-gallery-slider {
  display: flex;
  gap: 20px;

  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;

  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;

  scrollbar-width: none;
  -ms-overflow-style: none;
}

.demirsan-gallery-slider::-webkit-scrollbar {
  display: none;
}


/* BÜYÜK EKRAN: TAM 2 GÖRSEL */
.demirsan-gallery-item {
  flex: 0 0 calc((100% - 20px) / 2);
  width: calc((100% - 20px) / 2);

  padding: 0 !important;
  margin: 0 !important;

  border: 0 !important;
  border-radius: 0 !important;

  background: transparent !important;
  box-shadow: none !important;

  cursor: pointer;

  scroll-snap-align: start;

  appearance: none;
  -webkit-appearance: none;
}

.demirsan-gallery-item:hover {
  transform: none !important;
  box-shadow: none !important;
}


/* RESİM */
.demirsan-gallery-item-image {
  width: 100%;
  height: auto;

  padding: 0 !important;
  margin: 0 !important;

  background: transparent !important;

  display: block;
}

.demirsan-gallery-item-image img {
  display: block;

  width: 100%;
  height: auto;

  max-width: 100%;

  object-fit: contain;
}


/* TABLET + TELEFON: TAM 1 GÖRSEL */
@media (max-width: 991px) {
  .demirsan-gallery-item {
    flex: 0 0 100%;
    width: 100%;
  }
}
  /* =========================================
   İLK HÜCRE (ÜRÜN) HOVER DÜZELTME
========================================= */

.demirsan-editor-content
table
tbody
tr:first-child:hover
td:first-child {
  background: #e20614 !important;
  color: #ffffff !important;
  box-shadow: none !important;
}

/* İlk satır hover olunca sağ hücre de gereksiz değişmesin */
.demirsan-editor-content
table
tbody
tr:first-child:hover
td:not(:first-child) {
  background: rgba(226, 6, 20, 0.06) !important;
  color: #1a1a1a !important;
}
  .demirsan-editor-content table {
  border: 1px solid rgba(226, 6, 20, 0.35) !important;
}
  .demirsan-editor-content table td,
.demirsan-editor-content table th {
  border-right: 1px solid rgba(226, 6, 20, 0.22) !important;
  border-bottom: 1px solid rgba(226, 6, 20, 0.22) !important;
}
  .demirsan-editor-content table tbody tr td:first-child {
  border-right: 1px solid rgba(226, 6, 20, 0.55) !important;
}
  
body .demirsan-editor-content table td,
body .demirsan-editor-content table th,
body .demirsan-editor-content table td *,
body .demirsan-editor-content table th * {
  font-size: 13px !important;
}
  
/* ==========================================
   SIDEBAR MENÜ HOVER TİTREMESİ DÜZELTME
========================================== */

/* Menü elemanları sabit konumda kalsın */
body .demirsan-category-widget .single-categories li,
body .demirsan-related-widget .single-categories li {
  position: relative !important;
  transform: none !important;
  transition: none !important;
}

/* Linklerin boyutları hover sırasında değişmesin */
body .demirsan-category-widget .single-categories li a,
body .demirsan-related-widget .single-categories li a {
  display: flex !important;
  align-items: center !important;
  width: 100% !important;
  box-sizing: border-box !important;

  transform: none !important;
  transition: background-color 0.15s ease,
              color 0.15s ease !important;

  border: 0 !important;
  outline-offset: 0 !important;
}

/* Hover sırasında ölçü ve konum sabit */
body .demirsan-category-widget .single-categories li a:hover,
body .demirsan-related-widget .single-categories li a:hover {
  background-color: #e20614 !important;
  color: #ffffff !important;

  transform: none !important;
  margin: 0 !important;
  border: 0 !important;
}

/* Tema pseudo-element kaynaklı hareketleri engelle */
body .demirsan-category-widget .single-categories li a::before,
body .demirsan-category-widget .single-categories li a::after,
body .demirsan-related-widget .single-categories li a::before,
body .demirsan-related-widget .single-categories li a::after {
  display: none !important;
}

/* Hover sırasında yazılar ve oklar beyaz */
body .demirsan-category-widget .single-categories li a:hover span,
body .demirsan-category-widget .single-categories li a:hover i,
body .demirsan-related-widget .single-categories li a:hover span,
body .demirsan-related-widget .single-categories li a:hover i {
  color: #ffffff !important;
}


        `}
      </style>

      {loading ? (
        <div className="container">
          <div className="demirsan-detail-loading">Yükleniyor...</div>
        </div>
      ) : error ? (
        <div className="container">
          <div className="demirsan-detail-error">{error}</div>
        </div>
      ) : product ? (
        <div className="rts-service-details-area rts-section-gap">
          <div className="container">
            <div className="row">
              {/* ========================================
      SOL TARAF - KATEGORİLER + DİĞER ÜRÜNLER
  ========================================= */}
              <div className="col-xl-4 col-md-12 col-sm-12 col-12 pr--50 pr_md--0 pr_sm--0">
                <div className="rts-single-wized Categories service demirsan-category-widget">
                  <div className="wized-header">
                    <h5 className="title">Ürün Kategorileri</h5>
                  </div>

                  <div className="wized-body">
                    {categories.map((category) => {
                      const categoryTitle =
                        category.title || category.name || "Kategori";

                      const categorySlug = `${createSlug(
                        categoryTitle,
                      )}-${category.id}`;

                      const isActive = productCategoryIds.includes(
                        Number(category.id),
                      );

                      return (
                        <ul className="single-categories" key={category.id}>
                          <li>
                            <Link
                              className={isActive ? "active-category" : ""}
                              to={withLang(`/our-service/${categorySlug}`)}
                            >
                              <span>{categoryTitle}</span>

                              <i className="far fa-long-arrow-right" />
                            </Link>
                          </li>
                        </ul>
                      );
                    })}
                  </div>
                </div>

                {/* ========================================
        AYNI KATEGORİDEKİ DİĞER ÜRÜNLER
    ========================================= */}
                <div className="rts-single-wized Categories service demirsan-related-widget mt--30">
                  <div className="wized-header">
                    <h5 className="title">Müşterilerimizin Uygulamaları</h5>
                  </div>

                  <div className="wized-body">
                    {relatedLoading ? (
                      <div className="related-products-loading">
                        Yükleniyor...
                      </div>
                    ) : relatedProducts.length > 0 ? (
                      relatedProducts.map((relatedProduct) => {
                        const relatedTitle =
                          relatedProduct.title ||
                          relatedProduct.data?.title ||
                          "Ürün";

                        const relatedSlug = `${createSlug(
                          relatedTitle,
                        )}-${relatedProduct.id}`;

                        const isActiveProduct =
                          Number(relatedProduct.id) === Number(product?.id);

                        return (
                          <ul
                            className="single-categories"
                            key={relatedProduct.id}
                          >
                            <li>
                              <Link
                                className={
                                  isActiveProduct ? "active-category" : ""
                                }
                                to={withLang(`/service-details/${relatedSlug}`)}
                              >
                                <span>{relatedTitle}</span>

                                <i className="far fa-long-arrow-right" />
                              </Link>
                            </li>
                          </ul>
                        );
                      })
                    ) : (
                      <div className="related-products-empty">
                        Bu kategoride başka ürün bulunmuyor.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ========================================
      SAĞ TARAF - ÜRÜN DETAYI
  ========================================= */}
              <div className="col-xl-8 col-md-12 col-sm-12 col-12">
                <div className="service-detials-step-1">
                  {productImage && (
                    <div className="demirsan-detail-image">
                      <img src={productImage} alt={title} />
                    </div>
                  )}

                  <h1 className="title demirsan-detail-title">{title}</h1>

                  {editorContent ? (
                    <div
                      ref={editorRef}
                      className="demirsan-editor-content"
                      dangerouslySetInnerHTML={{
                        __html: editorContent,
                      }}
                    />
                  ) : (
                    <p className="disc">
                      Bu ürün için henüz detaylı içerik girilmemiştir.
                    </p>
                  )}
                  {galleryImages.length > 0 && (
                    <div className="demirsan-gallery-section">
                      <div className="demirsan-gallery-header">
                        <h3 className="demirsan-gallery-title">Galeri</h3>

                        {galleryImages.length > 1 && (
                          <div className="demirsan-gallery-actions">
                            <button
                              type="button"
                              className="demirsan-gallery-scroll-btn"
                              onClick={() => scrollGallery("left")}
                              aria-label="Galeriyi sola kaydır"
                            >
                              <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M15 18l-6-6 6-6" />
                              </svg>
                            </button>

                            <button
                              type="button"
                              className="demirsan-gallery-scroll-btn"
                              onClick={() => scrollGallery("right")}
                              aria-label="Galeriyi sağa kaydır"
                            >
                              <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M9 6l6 6-6 6" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>

                      <div
                        className="demirsan-gallery-slider"
                        ref={gallerySliderRef}
                      >
                        {galleryImages.map((image, index) => (
                          <button
                            type="button"
                            key={`${image.src}-${index}`}
                            className="demirsan-gallery-item"
                            onClick={() => openGalleryImage(index)}
                            aria-label={`Galeri görselini aç ${index + 1}`}
                          >
                            <div className="demirsan-gallery-item-image">
                              <img
                                src={image.src}
                                alt={image.alt || `${title} galeri görseli`}
                              />
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {activeGalleryIndex !== null && galleryImages[activeGalleryIndex] && (
        <div className="demirsan-gallery-lightbox" onClick={closeGalleryImage}>
          <button
            type="button"
            className="demirsan-gallery-close"
            onClick={closeGalleryImage}
            aria-label="Galeriyi kapat"
          >
            ×
          </button>

          {galleryImages.length > 1 && (
            <button
              type="button"
              className="demirsan-gallery-nav-btn prev"
              onClick={(e) => {
                e.stopPropagation();
                showPrevGalleryImage();
              }}
              aria-label="Önceki görsel"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
          )}

          <div
            className="demirsan-gallery-lightbox-content"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={galleryImages[activeGalleryIndex].src}
              alt={
                galleryImages[activeGalleryIndex].alt ||
                `${title} galeri görseli`
              }
            />
          </div>

          {galleryImages.length > 1 && (
            <button
              type="button"
              className="demirsan-gallery-nav-btn next"
              onClick={(e) => {
                e.stopPropagation();
                showNextGalleryImage();
              }}
              aria-label="Sonraki görsel"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M9 6l6 6-6 6" />
              </svg>
            </button>
          )}
        </div>
      )}
      <QuoteSection />
      <FooterOne />
    </div>
  );
}

export default ServiceDetails;
